import { track, EVENTS } from '@/lib/mixpanel';
import { supabase, type Profile } from './supabase';
import { emailLanguage } from './utils';

// Get the API URL from environment variables
const API_URL = import.meta.env.VITE_WORKER_URL || '';

/**
 * Creates a user profile in the database after successful authentication
 */
export const createUserProfile = async (user: any, session: any): Promise<void> => {
  // Get user info from provider (Google or Apple)
  const googleIdentity = user.identities?.find((identity: any) => identity.provider === 'google');
  const appleIdentity = user.identities?.find((identity: any) => identity.provider === 'apple');

  let newProfile: Omit<Profile, 'created_at' | 'updated_at' | 'country' | 'is_deleted'>;

  // TODO: supabase doen't return family_name and given_name
  // so we'll need to manuall call https://www.googleapis.com/oauth2/v3/userinfo with session.provider_token
  if (googleIdentity) {
    // Handle Google sign-in
    const userData = googleIdentity.identity_data;
    newProfile = {
      id: user.id,
      username: userData?.full_name || user.email?.split('@')[0] || '',
      family_name: userData?.family_name || '',
      given_name: userData?.given_name || '',
      email: user.email || '',
      source: 'web_checkout',
      user_identifier: '', // Empty for Google
      language: emailLanguage()
    };
  } else if (appleIdentity) {
    // Handle Apple sign-in
    const userData = appleIdentity.identity_data;
    newProfile = {
      id: user.id,
      username: userData?.name?.firstName ?
        `${userData.name.firstName} ${userData.name.lastName || ''}`.trim() :
        (user.email?.split('@')[0] || ''),
      family_name: userData?.name?.lastName || '',
      given_name: userData?.name?.firstName || '',
      email: user.email || '',
      source: 'web_checkout',
      user_identifier: appleIdentity.id || '', // Use Apple's unique identifier
      language: emailLanguage()
    };
  } else {
    // Fallback for unknown provider
    newProfile = {
      id: user.id,
      username: user.email?.split('@')[0] || '',
      family_name: '',
      given_name: '',
      email: user.email || '',
      source: 'web_checkout',
      user_identifier: '',
      language: emailLanguage()
    };
  }

  // Try to create profile, but don't fail if it doesn't work
  try {
    const response = await fetch(`${API_URL}/api/create-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ profile: newProfile })
    });

    if (!response.ok) {
      const error = await response.json();
      console.warn('Failed to create profile:', error.message);
    }
  } catch (error) {
    // Log error but don't prevent sign in
    console.warn('Error during profile creation:', error);
  }
};

/**
 * Checks if a user profile exists in the database
 */
export const checkProfileExists = async (userId: string): Promise<boolean> => {
  try {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select()
      .eq('id', userId)
      .limit(1);

    if (error) {
      console.warn('Error checking profile:', error);
      return false;
    }

    return profiles && profiles.length > 0;
  } catch (error) {
    console.warn('Error checking profile existence:', error);
    return false;
  }
};

/**
 * Handles all post-authentication tasks (profile creation, tracking, etc.)
 */
export const handlePostAuthTasks = async (user: any, session?: any, method: 'one_tap' | 'redirect' | 'google_button' = 'redirect'): Promise<void> => {
  try {
    // Check if profile exists
    const profileExists = await checkProfileExists(user.id);
    const isNewProfile = !profileExists;

    // Create profile if it doesn't exist
    if (isNewProfile && session) {
      await createUserProfile(user, session);
    }

    // Track successful sign in
    track(EVENTS.SIGN_IN, {
      provider: user.app_metadata?.provider,
      email: user.email,
      isNewProfile,
      method // 'one_tap' or 'redirect'
    });

    console.log('Post-auth tasks completed for user:', user.id, 'method:', method);
  } catch (error) {
    console.error('Error in post-auth tasks:', error);
    // Don't throw - these tasks are nice-to-have but shouldn't block sign-in
  }
};

/**
 * Gets the current session from Supabase
 */
export const getCurrentSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    return session;
  } catch (error) {
    console.error('Error getting current session:', error);
    return null;
  }
};
